import axios from 'axios';
import { debugLog } from '../utils/debugLog';

const formatDate = (dateStr) => {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  return date.toISOString().slice(0, 19);
};

function buildEventPayload(eventData) {
  console.log('[DEBUG FRONT] buildEventPayload input status:', eventData.status);
  const payload = {
    name: eventData.name,
    startDate: formatDate(eventData.startDate),
    endDate: formatDate(eventData.endDate),
    description: eventData.description || '',
    status: (eventData.status === 'approved' || eventData.status === 'cancelled') ? eventData.status : 'not_approved'
  };
  console.log('[DEBUG FRONT] buildEventPayload output status:', payload.status);
  if (eventData.organizationId != null && eventData.organizationId !== '') payload.organizationId = parseInt(eventData.organizationId, 10);
  if (eventData.sportTypeId != null && eventData.sportTypeId !== '') payload.sportTypeId = parseInt(eventData.sportTypeId, 10);
  payload.cloudUrl = eventData.cloudUrl != null && String(eventData.cloudUrl).trim() !== '' ? String(eventData.cloudUrl).trim() : null;
  if (Array.isArray(eventData.objectIds) && eventData.objectIds.length > 0) {
    payload.objectIds = eventData.objectIds.map(id => parseInt(id, 10)).filter(Boolean);
  }
  if (eventData.objectDates && typeof eventData.objectDates === 'object') {
    const od = {};
    for (const oid of Object.keys(eventData.objectDates)) {
      const d = eventData.objectDates[oid];
      od[oid] = {
        startDate: d && d.startDate ? formatDate(d.startDate) : null,
        endDate: d && d.endDate ? formatDate(d.endDate) : null
      };
    }
    payload.objectDates = od;
  }
  return payload;
}

function toCamelCase(str) {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

function transformToCamelCase(obj) {
  if (Array.isArray(obj)) {
    return obj.map(item => transformToCamelCase(item));
  }
  if (obj !== null && typeof obj === 'object') {
    const result = {};
    for (const key of Object.keys(obj)) {
      const camelKey = toCamelCase(key);
      result[camelKey] = transformToCamelCase(obj[key]);
    }
    return result;
  }
  return obj;
}

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  headers: {
    'Content-Type': 'application/json'
  }
});

// Добавление токена к запросам
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Лог исходящих запросов для дебаг-окна
api.interceptors.request.use((config) => {
  debugLog.push({
    type: 'api_request',
    method: (config.method || '').toUpperCase(),
    url: config.url,
    baseURL: config.baseURL,
    data: config.data,
    params: config.params,
  });
  return config;
});

// Обработка ответов для автоматической обработки ошибок + лог ответов
api.interceptors.response.use(
  (response) => {
    response.data = transformToCamelCase(response.data);
    debugLog.push({
      type: 'api_response',
      method: (response.config?.method || '').toUpperCase(),
      url: response.config?.url,
      status: response.status,
      data: response.data,
    });
    return response;
  },
  (error) => {
    debugLog.push({
      type: 'api_response',
      method: (error.config?.method || '').toUpperCase(),
      url: error.config?.url,
      status: error.response?.status,
      error: error.response?.data ?? error.message,
    });
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// API методы

export const authAPI = {
  register: async (username, password) => {
    const response = await api.post('/register', { username, password });
    return response.data;
  },

  login: async (username, password) => {
    const response = await api.post('/login', { username, password });
    // Сохраняем роль пользователя
    if (response.data.role) {
      localStorage.setItem('userRole', response.data.role);
    }
    return response.data;
  }
};

export const usersAPI = {
  getAll: async () => {
    const response = await api.get('/users');
    return response.data;
  },

  create: async (username, password, role = 'user', email, phone, organizationId) => {
    const response = await api.post('/users', { username, password, role, email, phone, organization_id: organizationId });
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.put(`/users/${id}`, data);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/users/${id}`);
    return response.data;
  },

  getObjects: async (userId) => {
    const response = await api.get(`/users/${userId}/objects`);
    return response.data;
  },

  getMe: async () => {
    const response = await api.get('/users/me');
    return response.data;
  },

  updateMe: async (data) => {
    const response = await api.put('/users/me', data);
    return response.data;
  },

  changePassword: async (newPassword) => {
    const response = await api.put('/users/me/password', { newPassword });
    return response.data;
  }
};

export const objectManagersAPI = {
  getManagers: async (objectId) => {
    const response = await api.get(`/objects/${objectId}/managers`);
    return response.data;
  }
};

export const objectsAPI = {
  getAll: async () => {
    const response = await api.get('/objects');
    return response.data;
  },

  create: async (name, managerIds = null) => {
    const response = await api.post('/objects', { name, managerIds: managerIds || [] });
    return response.data;
  },

  update: async (id, name, managerIds = null) => {
    const response = await api.put(`/objects/${id}`, { name, managerIds: managerIds || [] });
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/objects/${id}`);
    return response.data;
  }
};

export const eventsAPI = {
  create: async (objectId, eventData) => {
    const payload = buildEventPayload(eventData);
    const response = await api.post(`/objects/${objectId}/events`, payload);
    return response.data;
  },

  update: async (eventId, eventData) => {
    const payload = buildEventPayload(eventData);
    const response = await api.put(`/events/${eventId}`, payload);
    return response.data;
  },

  delete: async (eventId) => {
    const response = await api.delete(`/events/${eventId}`);
    return response.data;
  },

  exportToExcel: async (startDate, endDate) => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    const response = await api.get(`/events/export?${params.toString()}`);
    return response.data;
  }
};

export const eventAuditAPI = {
  getList: async (params = {}) => {
    const response = await api.get('/admin/event-audit', { params });
    return response.data;
  },
  restore: async (eventId) => {
    const response = await api.post(`/admin/events/${eventId}/restore`);
    return response.data;
  }
};

export const holidaysAPI = {
  getHolidays: async (year) => {
    const response = await api.get(`/holidays/${year}`);
    return response.data;
  }
};

// Расписание секций
export const scheduleAPI = {
  getData: async () => {
    const response = await api.get('/schedule/data');
    return response.data;
  },

  getPremises: async (objectId) => {
    const response = await api.get(`/objects/${objectId}/premises`);
    return response.data;
  },

  createPremise: async (objectId, name) => {
    const response = await api.post(`/objects/${objectId}/premises`, { name });
    return response.data;
  },

  updatePremise: async (id, name) => {
    const response = await api.put(`/premises/${id}`, { name });
    return response.data;
  },

  deletePremise: async (id) => {
    const response = await api.delete(`/premises/${id}`);
    return response.data;
  },

  getSections: async () => {
    const response = await api.get('/sections');
    return response.data;
  },

  createSection: async (data) => {
    const response = await api.post('/sections', data);
    return response.data;
  },

  updateSection: async (id, data) => {
    const response = await api.put(`/sections/${id}`, data);
    return response.data;
  },

  deleteSection: async (id) => {
    const response = await api.delete(`/sections/${id}`);
    return response.data;
  },

  getSectionSchedule: async (params = {}) => {
    const response = await api.get('/section-schedule', { params });
    return response.data;
  },

  createSectionSchedule: async (data) => {
    const response = await api.post('/section-schedule', {
      sectionId: data.sectionId,
      premiseId: data.premiseId,
      startDate: data.startDate,
      endDate: data.endDate
    });
    return response.data;
  },

  updateSectionSchedule: async (id, data) => {
    const response = await api.put(`/section-schedule/${id}`, data);
    return response.data;
  },

  deleteSectionSchedule: async (id) => {
    const response = await api.delete(`/section-schedule/${id}`);
    return response.data;
  },

  // Шаблон недельного расписания (для каждого тренера свой шаблон)
  getScheduleTemplates: async (objectId, coachId = null) => {
    const url = coachId != null && coachId !== ''
      ? `/objects/${objectId}/schedule-templates?coach_id=${encodeURIComponent(coachId)}`
      : `/objects/${objectId}/schedule-templates`;
    const response = await api.get(url);
    return response.data;
  },
  createScheduleTemplate: async (objectId, valid_from, valid_to, coach_id = null) => {
    const response = await api.post(`/objects/${objectId}/schedule-templates`, { valid_from, valid_to, coach_id });
    return response.data;
  },
  getScheduleTemplate: async (id) => {
    const response = await api.get(`/schedule-templates/${id}`);
    return response.data;
  },
  updateScheduleTemplate: async (id, valid_from, valid_to, coach_id = null, enabled = undefined) => {
    const body = { valid_from, valid_to, coach_id };
    if (enabled !== undefined) body.enabled = enabled;
    const response = await api.put(`/schedule-templates/${id}`, body);
    return response.data;
  },
  deleteScheduleTemplate: async (id) => {
    const response = await api.delete(`/schedule-templates/${id}`);
    return response.data;
  },
  getScheduleTemplateSlots: async (templateId) => {
    const response = await api.get(`/schedule-templates/${templateId}/slots`);
    return response.data;
  },
  createScheduleTemplateSlot: async (templateId, data) => {
    const response = await api.post(`/schedule-templates/${templateId}/slots`, data);
    return response.data;
  },
  updateScheduleTemplateSlot: async (slotId, data) => {
    const response = await api.put(`/schedule-template-slots/${slotId}`, data);
    return response.data;
  },
  deleteScheduleTemplateSlot: async (slotId) => {
    const response = await api.delete(`/schedule-template-slots/${slotId}`);
    return response.data;
  },
  approveScheduleTemplate: async (templateId) => {
    const response = await api.post(`/schedule-templates/${templateId}/approve`);
    return response.data;
  },
  saveScheduleTemplateChanges: async (templateId) => {
    const response = await api.post(`/schedule-templates/${templateId}/save-changes`);
    return response.data;
  }
};

// Справочники: организации, виды спорта, тренеры
export const referencesAPI = {
  getOrganizations: async () => {
    const response = await api.get('/organizations');
    return response.data;
  },
  createOrganization: async (name) => {
    const response = await api.post('/organizations', { name });
    return response.data;
  },
  updateOrganization: async (id, name) => {
    const response = await api.put(`/organizations/${id}`, { name });
    return response.data;
  },
  deleteOrganization: async (id) => {
    const response = await api.delete(`/organizations/${id}`);
    return response.data;
  },
  getSportTypes: async (organizationId) => {
    const params = organizationId ? { organization_id: organizationId } : {};
    const response = await api.get('/sport-types', { params });
    return response.data;
  },
  createSportType: async (organizationId, name) => {
    const response = await api.post('/sport-types', { organization_id: organizationId, name });
    return response.data;
  },
  updateSportType: async (id, name) => {
    const response = await api.put(`/sport-types/${id}`, { name });
    return response.data;
  },
  deleteSportType: async (id) => {
    const response = await api.delete(`/sport-types/${id}`);
    return response.data;
  },
  getCoaches: async (sportTypeId) => {
    const params = sportTypeId ? { sport_type_id: sportTypeId } : {};
    const response = await api.get('/coaches', { params });
    return response.data;
  },
  getCoachesFull: async () => {
    const response = await api.get('/references/coaches-full');
    return response.data;
  },
  createCoach: async (sportTypeId, name) => {
    const response = await api.post('/coaches', { sport_type_id: sportTypeId, name });
    return response.data;
  },
  updateCoach: async (id, name) => {
    const response = await api.put(`/coaches/${id}`, { name });
    return response.data;
  },
  deleteCoach: async (id) => {
    const response = await api.delete(`/coaches/${id}`);
    return response.data;
  }
};

export default api;
